import { BusinessChallenges } from '@platform/client';
import { BusinessChallenge, ChallengeUpdateArgsChallengeNumber } from '@platform/client/schemas';
import { Button, Icon, PillBadge } from '@platform/components';
import cx from 'classix';
import { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { navigate } from 'wouter/use-browser-location';
import { DeleteChallengeModal } from './DeleteChallengeModal';
import { businessChallengeFullText } from '../../util/TextHelpers';
import { useApplicationLocalStorage } from '../../application/application-hook';
import EditModal, { ViewModeProps } from './edit-modal/EditModal';

const MAX_CHALLENGES = 3;
const MAX_CONTEXT_CHARS = 115;

interface AplicationStorageProps {
  value: boolean;
};

interface ChallengeInterface {
  challengeNumber: ChallengeUpdateArgsChallengeNumber,
}

const BusinessChallengesSection = ({ organizationId }: { organizationId: number }) => {
  const [deleteChallengeNumber, setDeleteChallengeNumber] = useState<number>();
  const {
    data: challengeData,
    isSuccess,
    isLoading,
  } = BusinessChallenges.useGetOrganizationBusinessChallenges(organizationId);
  const existingChallenges = (challengeData?.challenges ?? []).filter(
    (challenge) => challenge.challenge && challenge.challengeContext,
  );
  const { getApplicationDataByKey, updateApplicationData } = useApplicationLocalStorage<AplicationStorageProps>('challenge-help')

  const showHelpStorage = [undefined, true].includes(getApplicationDataByKey("value"));
  const [showHelp, setShowHelp] = useState(showHelpStorage);
  const [modalOpen, setModalOpen] = useState(false);
  const [challengeValue, setChallengeValue] = useState<string>("");
  const [challengeContext, setChallengeContext] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewModeProps>("view");
  const [challengeNumber, setChallengeNumber] = useState<ChallengeUpdateArgsChallengeNumber>(1);

  const storageKey = `project_suggestion_${organizationId}_${challengeData?.challengeId}_${deleteChallengeNumber}`;

  const closeHelp = () => {
    updateApplicationData({ 'value': false });
    setShowHelp(false);
  }

  const openEditModal = (challenge: BusinessChallenge | ChallengeInterface, viewMode: ViewModeProps = "view") => {
    setChallengeNumber((challenge?.challengeNumber || 1) as ChallengeUpdateArgsChallengeNumber);
    setViewMode(viewMode);
    setChallengeValue(challenge && "challenge" in challenge && challenge.challenge ? challenge.challenge : "");
    setChallengeContext(challenge && "challengeContext" in challenge && challenge.challengeContext ? challenge.challengeContext : "");
    setModalOpen(true);
  }

  return (
    <div className='mt-8 mb-16 text-gray-700'>
      {modalOpen &&
        <EditModal
          challenge={challengeValue}
          challengeContext={challengeContext}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          viewMode={viewMode}
          organizationId={organizationId}
          challengeNumber={challengeNumber}
          challengeId={challengeData?.challengeId || 1}
        />
      }
      <ChallengesHeader completed={existingChallenges.length} />
      {showHelp && <ChallengeHelp setShowHelp={() => closeHelp()} />}
      <ChallengeCards
        challenges={challengeData?.challenges ?? []}
        isLoading={isLoading}
        isSuccess={isSuccess}
        setDeleteChallengeNumber={setDeleteChallengeNumber}
        openEditModal={(challenge: BusinessChallenge | ChallengeInterface, viewMode?: ViewModeProps) => openEditModal(challenge, viewMode)}
        organizationId={organizationId}
        challengeId={challengeData?.challengeId || 1}
      />
      {deleteChallengeNumber && (
        <DeleteChallengeModal
          isOpen={true}
          onClose={() => {
            setDeleteChallengeNumber(undefined);
            localStorage.removeItem(storageKey);
            localStorage.removeItem(storageKey + "_submitted");
          }}
          challengeNumber={deleteChallengeNumber}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};


const ChallengesHeader = ({ completed }: { completed: number }) => {
  return (
    <div className='w-full xl:w-[848px] flex gap-2 mb-8'>
      <div className='flex-grow'>
        <h2 className='text-display-xs break-words max-w-[75%] xl:max-w-full font-medium mb-2'>
          Get expert help for your challenges
        </h2>
        <p className='text-md mr-0.5'>
          Tell us your business challenges and we will generate a project for your review.
          A published project makes your needs visible to the experts best suited to solve them.
        </p>
      </div>
      <div className='w-16 flex flex-col items-end'>
        <h2 className='text-display-xs font-bold text-brand-500'>
          {completed}/{MAX_CHALLENGES}
        </h2>
        <p className='text-xs text-gray-500'>Completed</p>
      </div>
    </div>
  );
};

const ChallengeCards = ({
  challenges,
  isLoading,
  isSuccess,
  setDeleteChallengeNumber,
  openEditModal,
  organizationId,
  challengeId,
}: {
  challenges: BusinessChallenge[];
  isLoading: boolean;
  isSuccess: boolean;
  setDeleteChallengeNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
  openEditModal: (challenge: BusinessChallenge| ChallengeInterface, viewMode?: ViewModeProps) => void;
  organizationId: number;
  challengeId: number,
}) => {
  const oneToNarray = useMemo(
    () => Array.from({ length: MAX_CHALLENGES }, (_, index) => index + 1)
  , []);

  return (
    <div className='flex flex-col items-center xl:flex-row xl:justify-start flex-wrap gap-6'>
      {isLoading &&
        (oneToNarray)
        .map((challengeNumber) => (
          <ChallengeCardBackground key={challengeNumber}>
            <Skeleton height={190} />
          </ChallengeCardBackground>
        ))}
      {isSuccess &&
        challenges.map((challenge, index) => (
          <ChallengeCard
            key={challenge.challengeNumber}
            challengeId={challengeId}
            challengeIdx={index+1}
            challenge={challenge}
            setDeleteChallengeNumber={setDeleteChallengeNumber}
            openEditModal={openEditModal}
            organizationId={organizationId}
          />
        ))}
    </div>
  );
};

const ChallengeCard = ({
  challenge,
  setDeleteChallengeNumber,
  openEditModal,
  organizationId,
  challengeId,
  challengeIdx,
}: {
  setDeleteChallengeNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
  challenge: BusinessChallenge;
  openEditModal: (challenge: BusinessChallenge | ChallengeInterface, viewMode?: ViewModeProps) => void;
  organizationId: number,
  challengeId: number,
  challengeIdx: number,
}) => {
  const storageKey = `project_suggestion_${organizationId}_${challengeId}_${challengeIdx}`;
  const hasCachedSuggestion = (() => {
    try {
      return !!localStorage.getItem(storageKey);
    } catch {
      return false;
    }
  })();
  const isSuggestionSubmitted = (() => {
    try {
      return !!localStorage.getItem(storageKey + "_submitted");
    } catch {
      return false;
    }
  })();

  return (
    <>
      {challenge && challenge.challenge && challenge.challengeContext ?
        <ChallengeCardBackground>
          <div className='flex justify-between items-center pb-3'>
            <PillBadge variant='dot' color='blue' size='md'>
              Challenge {challenge.challengeNumber}
            </PillBadge>
            <div className='flex'>
            <Button
                onClick={() => setDeleteChallengeNumber(challenge.challengeNumber)}
                variant='tertiary-gray'
                size='sm'
                leadIcon='trash-01'
                leadIconClassNames='stroke-error-500'
                aria-label={`Delete challenge ${challenge.challengeNumber}`}
              >
                <></>
              </Button>
              <Button
                onClick={() => openEditModal(challenge, "edit")}
                variant='tertiary-gray'
                size='sm'
                leadIcon='edit-02'
                aria-label={`Edit challenge ${challenge.challengeNumber}`}
              >
                <></>
              </Button>
            </div>
          </div>
          {/* truncate on full screen when cards are smaller */}
          <p className='text-lg line-clamp-1 font-semibold pb-2 h-8'>
            {businessChallengeFullText(challenge.challenge, true)}
          </p>
          <p className='text-[14px]/[20px] font-normal wrap-break-word h-25'>
            {challenge.challengeContext.length > MAX_CONTEXT_CHARS
              ? <>{challenge.challengeContext.slice(0, MAX_CONTEXT_CHARS)}... <span
                  className='text-brand-500 cursor-pointer hover:text-brand-600'
                  onClick={() => openEditModal(challenge, "view")}
                >See more</span></>
              : challenge.challengeContext
            }
          </p>
          <div className='h-px w-full bg-gray-100 my-[14px]'></div>
          <div className='flex'>
            <Icon type="star-06" stroke="brand-500" />
            <p className="text-sm font-semibold ml-0.5">AI-suggested project</p>
          </div>
          <p className='text-xs pb-4 text-gray-500'>Tailored to your challenge and organization</p>

          {isSuggestionSubmitted ? (
            <div className='flex items-center gap-2 rounded-xl border border-success-100 bg-success-25 px-4 py-3'>
              <div className='w-6 h-6 rounded-full bg-success-100 flex items-center justify-center shrink-0'>
                <Icon type='check' size='14' stroke='success-500' />
              </div>
              <span className='text-sm font-semibold text-success-500'>Project submitted</span>
            </div>
          ) : (
            <Button
              variant='primary'
              size='xl'
              onPress={() => navigate(`/platform/dashboard/suggestion/${challengeId}/${challengeIdx}`)}
              leadIcon={hasCachedSuggestion ? 'file-06' : undefined}
            >
              {hasCachedSuggestion ? 'Resume project →' : 'Generate project'}
            </Button>
          )}
        </ChallengeCardBackground>
      : <ChallengeCardBlank
          challengeNumber={challenge?.challengeNumber}
          openEditModal={openEditModal}
        />
      }
    </>
  );
};

const ChallengeCardBlank = ({
  challengeNumber,
  openEditModal,
}: {
  challengeNumber: number;
  openEditModal: (challenge: BusinessChallenge | ChallengeInterface, viewMode?: ViewModeProps) => void;
}) => {
  return (
    <ChallengeCardBackground isBlank={true}>
      <button
        onClick={() => openEditModal({ challengeNumber: challengeNumber as ChallengeUpdateArgsChallengeNumber }, "create")}
        className='h-full w-full flex flex-col items-center justify-center gap-2'
        aria-label={`Add challenge ${challengeNumber}`}
      >
        <div className='border-1 h-12 w-12 rounded-full flex items-center justify-center'>
          <Icon type='plus' size='24' additionalClassNames='text-brand-500' />
        </div>
        <p>Add a challenge</p>
        <p className='text-xs text-gray-400'>What's your biggest priority right now?</p>
      </button>
    </ChallengeCardBackground>
  );
};

const ChallengeCardBackground = ({
  isBlank = false,
  children,
}: {
  isBlank?: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={cx(
        isBlank ? 'bg-white hover:bg-secondary-brand-25 hover:border-brand-500' : 'bg-white p-[18px]',
        'w-full xl:w-[268px] shadow-xs border-1 border-gray-200 rounded-[14px] flex flex-col relative h-94',
      )}
    >
      {
        isBlank ? null :
        <div className='bg-brand-500 h-98/100 w-1 overflow-hidden rounded-l-[14px] absolute top-1 left-0' />
      }
      {children}
    </div>
  );
};

const ChallengeHelp = ({ setShowHelp }:{ setShowHelp: () => void }) => {
  return (
    <div className='bg-secondary-brand-25 w-full h-[250px] min-[350px]:h-[200px] xl:h-[82px] border-1 border-gray-200 rounded-[14px] mb-8 flex justify-between'>
      <div className='w-29/30 h-full flex flex-col items-center xl:flex-row '>
        <div className='xl:h-full ml-1.5 flex items-center flex justify-center w-30 mt-2.5 xl:mt-0'>
          <Icon type="connect" size="25" stroke="brand-500" viewbox="0 0 13 24"/>
          <p className='font-bold text-[12px] text-brand-600 uppercase w-25'>HOW IT WORKS</p>
        </div>

        <div className='h-8/10 w-px bg-gray-100 mx-2.5 self-center hidden xl:block' />

        <div className='flex space-between flex-col xl:flex-row h-full items-center ml-0.5 xl:ml-0 overflow-hidden px-2'>
          <div className='flex flex-row xl:justify-center w-full mb-2.5 xl:mb-0 xl:w-55'>
            <span className='mr-2'>
              <p className='bg-brand-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-xs text-bold'>1</p>
            </span>
            <div className='flex flex-col'>
              <p className="text-sm font-semibold text-gray-700">Add a challenge</p>
              <p className="text-xs text-gray-500">Share a priority your organization is working on</p>
            </div>
          </div>
          <div className='flex flex-row xl:justify-center w-full mb-2.5 xl:mb-0 xl:w-55'>
            <span className='mr-2'>
              <p className='bg-brand-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-xs text-bold'>2</p>
            </span>
            <div className='flex flex-col'>
              <p className="text-sm font-semibold text-gray-700">Generate your project</p>
              <p className="text-xs text-gray-500">View and refine before submitting for review</p>
            </div>
          </div>
          <div className='flex flex-row xl:justify-center w-full mb-2.5 xl:mb-0 xl:w-55'>
            <span className='mr-2'>
              <p className='bg-brand-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-xs text-bold'>3</p>
            </span>
            <div className='flex flex-col'>
              <p className="text-sm font-semibold text-gray-700">Publish your project</p>
              <p className="text-xs text-gray-500">Once reviewed, your project is ready for expert support</p>
            </div>
          </div>
        </div>
      </div>

      <div className='mt-2 mr-5 sm:mr-2 cursor-pointer h-6 w-1/30' onClick={() => setShowHelp()}>
        <Icon type="x"/>
      </div>
    </div>
  )
}

export default BusinessChallengesSection;
